import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Share2, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';

export default function PublicMenu() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const menuUrl = `${window.location.origin}/menu`;

  useEffect(() => {
    const source = axios.CancelToken.source();
    const fetchMenus = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/menus`, { cancelToken: source.token });
        setMenus(res.data);
      } catch (err) {
        if (!axios.isCancel(err)) console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchMenus();
    return () => source.cancel();
  }, []);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(menuUrl);
    toast.success('Enlace copiado al portapapeles');
  };

  if (loading) return <div className="py-32 text-center text-slate-500 font-sans tracking-widest uppercase font-bold">Cargando menús...</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-16 sm:py-24 relative">
      <div className="text-center mb-24 relative">
        <h1 className="text-4xl sm:text-6xl font-serif text-slate-900 font-bold mb-8 tracking-[0.2em] uppercase">Catering Menu</h1>
        <div className="w-32 h-[3px] bg-orange-600 mx-auto mb-10"></div>
        
        <Button onClick={() => setShareOpen(true)} className="bg-transparent hover:bg-slate-900 hover:text-[#FDF9F1] text-slate-900 border-[3px] border-slate-900 rounded-sm px-8 py-3 mx-auto font-sans font-bold uppercase tracking-[0.2em] text-xs transition-colors">
          <Share2 className="mr-2 h-4 w-4" /> Compartir Menú
        </Button>
      </div>

      <div className="space-y-32">
        {menus.map((menu) => (
          <div key={menu._id} className="relative">
            <div className="mb-16 text-center">
              <h2 className="text-3xl sm:text-5xl font-serif font-bold text-slate-900 mb-6 tracking-[0.1em] uppercase">{menu.title}</h2>
              {menu.description && <p className="text-slate-800 text-sm sm:text-base font-sans font-semibold tracking-widest leading-loose max-w-2xl mx-auto uppercase">{menu.description}</p>}
              {menu.image_url && (
                <img src={menu.image_url.startsWith('http') ? menu.image_url : `${API_URL}${menu.image_url}`} alt={menu.title} className="w-full max-h-[500px] object-cover shadow-2xl mt-12 border-[12px] border-white" />
              )}
            </div>

            <div className="space-y-12">
              {menu.items?.map(item => (
                <div key={item.id} className="flex flex-col sm:flex-row justify-between items-start gap-8 border-b-2 border-slate-300/50 pb-12 last:border-0">
                  <div className="flex-1 w-full">
                    {item.category && <span className="text-xs uppercase tracking-[0.3em] text-orange-600 mb-4 block font-extrabold">{item.category}</span>}
                    <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4 mb-4">
                      <h3 className="font-serif italic font-bold text-slate-900 text-2xl sm:text-3xl uppercase tracking-wider">{item.name}</h3>
                      {item.price && <span className="font-serif italic font-bold text-slate-800 text-xl sm:text-2xl mt-1 sm:mt-0">{item.price}</span>}
                    </div>
                    <p className="text-slate-800 text-sm sm:text-base whitespace-pre-wrap font-sans font-bold leading-loose uppercase tracking-[0.05em]">{item.description}</p>
                  </div>
                  {item.image_url && (
                    <img src={item.image_url.startsWith('http') ? item.image_url : `${API_URL}${item.image_url}`} alt={item.name} className="w-full sm:w-56 sm:h-56 object-cover shadow-xl mt-4 sm:mt-0 border-[6px] border-white" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-sm rounded-none p-10 text-center bg-[#FDF9F1] border-4 border-slate-900">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-slate-900 tracking-[0.1em] uppercase font-bold">Compartir Menú</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center mt-6">
            <div className="p-4 bg-white border-2 border-slate-200 inline-block mb-8 shadow-xl">
              <QRCodeSVG value={menuUrl} size={180} bgColor={"#ffffff"} fgColor={"#0F172A"} level={"L"} />
            </div>
            <p className="text-xs font-sans tracking-widest uppercase font-bold text-slate-700 mb-6 text-center leading-relaxed">Escanea el código QR o copia el enlace a continuación.</p>
            
            <div className="flex items-center gap-2 w-full max-w-xs mx-auto">
              <Input value={menuUrl} readOnly className="bg-white text-slate-700 font-sans font-semibold text-sm rounded-none border-2 border-slate-300" />
              <Button onClick={copyToClipboard} variant="outline" className="px-4 rounded-none border-2 border-slate-300 hover:bg-slate-200 text-slate-900">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
